import { useStore } from 'effector-react'
import { addOutline, trashOutline } from 'ionicons/icons'
import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useCameraContext } from '~/widgets/CameraProvider/context'
import { ThumbType } from '~/shared/api'
import { IconButton } from '~/shared/ui/Buttons'
import { getNextPhotoConfig } from '~/shared/ui/form/PickPhotos/helpers'
import { myPalette } from '~/shared/ui/theme'
import { $savingImagesCount, saveImagesFx } from './model'
import {
  Title,
  Descriptions,
  ErrorMessage,
  ImgWrapper,
  DeleteButton,
  Grid,
} from './styled'

export type PhotoConfig = {
  title: string
  type: string
}

type PickImagesProps = {
  title?: string
  descriptions: string
  name: string
  multiple?: boolean
  fnUpload: (file: Blob) => Promise<ThumbType>
  maxPhotos?: number
  photoConfig?: PhotoConfig[]
}

export function PickPhotos({
  title = 'Добавить фото',
  descriptions,
  name,
  multiple = false,
  fnUpload,
  maxPhotos,
  photoConfig,
}: PickImagesProps) {
  const isUploading = !!useStore($savingImagesCount)?.[name]
  const { cameraOpen, cameraClose, setHelperText } = useCameraContext()
  const { getValues, watch } = useFormContext()

  const formPhotos: ThumbType[] = watch(name)
  const photos = useMemo<ThumbType[]>(() => formPhotos || [], [formPhotos])

  const nextPhoto = useMemo<PhotoConfig | null>(
    () => getNextPhotoConfig(photos, photoConfig),
    [photoConfig, photos],
  )

  useEffect(() => {
    setHelperText(nextPhoto?.title || null)
  }, [nextPhoto?.title, setHelperText])

  const handlePickPhoto = async (
    photo: Blob,
    formFnOnChange: (images: ThumbType[]) => void,
  ) => {
    if (!multiple) formFnOnChange([])
    const images = getValues(name) || []

    const { thumb } = await saveImagesFx({
      photo,
      fieldName: name,
      fnUpload,
    })

    const nextPhotoType = getNextPhotoConfig(images, photoConfig)?.type

    const newPhotos = multiple
      ? [
          ...images,
          { ...thumb, ...(nextPhotoType ? { type: nextPhotoType } : {}) },
        ]
      : [thumb]

    formFnOnChange(newPhotos)

    if (maxPhotos && newPhotos.length >= maxPhotos) {
      cameraClose()
    }
  }

  const helperCameraOpen = (formFnOnChange: (images: ThumbType[]) => void) => {
    const images = getValues(name) || []

    if (maxPhotos && images.length >= maxPhotos) {
      return
    }

    cameraOpen(async (file) => {
      await handlePickPhoto(file, formFnOnChange)
    })
  }

  return (
    <Controller
      name={name}
      defaultValue={[]}
      render={({
        field: { onChange, value: photos = [] },
        fieldState: { error, invalid },
      }) => {
        const disableAddButton =
          isUploading || (maxPhotos && photos?.length >= maxPhotos)

        return (
          <div data-testid={`pick-images-${name}`}>
            <Title>
              {title}
              <IconButton
                icon={addOutline}
                disabled={Boolean(disableAddButton)}
                loading={isUploading}
                data-testid='button-add'
                onClick={() => helperCameraOpen(onChange)}
                style={{ fontSize: '15px' }}
                progressBlockSize={27}
                iconProps={{
                  style: {
                    color: myPalette.midnightBlue.main,
                  },
                }}
              />
            </Title>
            {!photos?.length && (
              <Descriptions $invalid={invalid}>{descriptions}</Descriptions>
            )}

            {Boolean(photos?.length) && (
              <Grid>
                {photos.map((item: ThumbType) => (
                  <div key={item.uuid}>
                    <ImgWrapper data-testid={`image-${item.uuid}`}>
                      <img src={item.previewUrlDownload} alt='' />

                      <DeleteButton
                        icon={trashOutline}
                        data-testid={`button-delete-${item.uuid}`}
                        onClick={() =>
                          onChange(
                            photos.filter(
                              (image: ThumbType) => image.uuid !== item.uuid,
                            ),
                          )
                        }
                        style={{ fontSize: '12px' }}
                        iconProps={{
                          style: {
                            color: myPalette.coral.main,
                          },
                        }}
                      />
                    </ImgWrapper>
                  </div>
                ))}
              </Grid>
            )}

            {invalid && (
              <ErrorMessage data-testid='error-message'>
                {error?.message || 'Обязательное поле'}
              </ErrorMessage>
            )}
          </div>
        )
      }}
    />
  )
}
